import styles from './Modal.module.scss';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Authorization, TrelloPowerUpContext } from '@powerupsclub/trello';
import { PaymentsContext } from '@powerupsclub/payments';
import Spinner from '@atlaskit/spinner';
import { Redirect } from 'react-router-dom';
import StackGrid from 'react-stack-grid';
import isImage from 'is-image';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';

export const Modal = () => {
  const {
    isLoading: isLoadingTrello,
    isAuthorized,
    token,
    trelloIframe,
  } = useContext(TrelloPowerUpContext);
  const { isLoading: isLoadingPayments, shouldShowPaymentModal } =
    useContext(PaymentsContext);

  const [cardsWithAttachments, setCardsWithAttachments] = useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [boardData, setBoardData] = useState<any>();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [columnWidth, setColumnWidth] = useState(150);
  const [gutterWidth, setGutterWidth] = useState(5);
  const [gutterHeight, setGutterHeight] = useState(5);
  const [alwaysShowName, setAlwaysShowName] = useState(false);
  const [mediaCardNameField, setMediaCardNameField] = useState('attachment');

  const [hiddenAttachmentIds, setHiddenAttachmentIds] = useState<string[]>([]);
  const [seeHiddenImages, setSeeHiddenImages] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const queryStrings = Object.fromEntries(urlSearchParams.entries());
  const isGuest = queryStrings.isGuest === 'true';

  const isTrial = queryStrings.isTrial === 'true';

  const getBoardData = useCallback(async () => {
    if (isAuthorized && trelloIframe.isMemberSignedIn()) {
      const board = await trelloIframe.board('id');
      window.Trello.setToken(token);
      const _boardData = await window.Trello.get(`boards/${board.id}`, {
        filter: 'visible',
        fields: 'id,labels,name,prefs',
        attachments: true,
      }).promise();

      trelloIframe.set('board', 'shared', 'boardData', _boardData);

      return _boardData;
    } else {
      let _boardData = trelloIframe.get('board', 'shared', 'boardData');

      if (!_boardData) {
        _boardData = {
          prefs: {
            backgroundColor: '#ffffff',
          },
        };
      }

      return _boardData;
    }
  }, [isAuthorized, token, trelloIframe]);

  useEffect(() => {
    async function fetchAttachments() {
      setIsLoadingAttachments(true);

      try {
        const [cards, _boardData, _gallerySettings, _hiddenAttachmentIds] =
          await Promise.all([
            trelloIframe.cards('all'),
            getBoardData(),
            trelloIframe.get('board', 'shared', 'gallery-view-settings'),
            trelloIframe.get(
              'board',
              'shared',
              'gallery-view-hidden-attachments'
            ),
          ]);

        if (_gallerySettings) {
          setColumnWidth(_gallerySettings.columnWidth);
          setGutterWidth(_gallerySettings.gutterWidth);
          setGutterHeight(_gallerySettings.gutterHeight);
          setAlwaysShowName(_gallerySettings.alwaysShowName);
          setMediaCardNameField(_gallerySettings.mediaCardNameField);
        }

        if (_hiddenAttachmentIds) {
          setHiddenAttachmentIds(_hiddenAttachmentIds);
        }

        const _cardsWithAttachments = cards.filter((card: any) => {
          return card.attachments.length > 0;
        });

        setCardsWithAttachments(_cardsWithAttachments);
        setBoardData(_boardData);

        const accentColor =
          _boardData?.prefs?.backgroundTopColor ||
          _boardData?.prefs?.backgroundColor;

        trelloIframe.updateModal({
          accentColor,
          ...(trelloIframe.memberCanWriteToModel('board')
            ? {
                actions: [
                  {
                    icon: './gear.png',
                    alt: 'Settings',
                    position: 'left',
                    callback: (y: any) => {
                      window.postMessage('gallery-view-settings', '*');
                    },
                  },
                  {
                    icon: './eye.png',
                    alt: 'Toggle hidden images',
                    position: 'left',
                    callback: (y: any) => {
                      window.postMessage('gallery-view-toggle-hidden', '*');
                    },
                  },
                  {
                    icon: './loudspeaker.png',
                    alt: 'Give feedback',
                    position: 'right',
                    callback: (y: any) => {
                      y.popup({
                        title: y.localizeKey('Give us feedback'),
                        url: './feedback',
                        height: 350,
                      });
                    },
                  },
                ],
              }
            : {}),
        });
      } catch (e) {
        console.log(e);
      }

      setIsLoadingAttachments(false);
    }

    if (isGuest) {
      fetchAttachments();
    } else if (isAuthorized) {
      fetchAttachments();
    }
  }, [getBoardData, isAuthorized, isGuest, token, trelloIframe]);

  const handleSettingsMessage = (event: any) => {
    if (event.data === 'gallery-view-settings') {
      setIsSettingsOpen(!isSettingsOpen);
    }

    if (event.data === 'gallery-view-toggle-hidden') {
      setSeeHiddenImages(!seeHiddenImages);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleSettingsMessage);

    return () => window.removeEventListener('message', handleSettingsMessage);
  });

  useEffect(() => {
    if (isDirty) {
      trelloIframe.set('board', 'shared', 'gallery-view-settings', {
        columnWidth,
        gutterWidth,
        gutterHeight,
        alwaysShowName,
        mediaCardNameField,
      });
    }
  }, [
    trelloIframe,
    columnWidth,
    gutterWidth,
    gutterHeight,
    alwaysShowName,
    mediaCardNameField,
    isDirty,
  ]);

  const isLoading =
    isLoadingTrello || isLoadingPayments || isLoadingAttachments;

  if (isLoading) {
    return (
      <div className={styles.loadingSpinnerContainer}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!isGuest) {
    if (!isAuthorized) {
      return <Authorization heading="Gallery View" imageSrc="./logo.png" />;
    }

    if (shouldShowPaymentModal && !isTrial) {
      return <Redirect to="/payment" />;
    }
  }

  const mediaCards: any = [];

  cardsWithAttachments.forEach((card: any) => {
    card.attachments.forEach((attachment: any) => {
      if (
        !attachment.url.endsWith('.svg') &&
        isImage(attachment.url) &&
        (seeHiddenImages || !hiddenAttachmentIds.includes(attachment.id))
      ) {
        const mediaCard = {
          ...card,
          attachment,
          isHidden: hiddenAttachmentIds.includes(attachment.id),
        };

        delete mediaCard.attachments;

        mediaCards.push(mediaCard);
      }
    });
  });

  const backgroundColor =
    boardData?.prefs?.backgroundTopColor || boardData?.prefs?.backgroundColor;

  const backgroundImage = boardData?.prefs?.backgroundImage;

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: '50%',
        backgroundColor,
      }}
    >
      {isSettingsOpen && (
        <div className={styles.settingsContainer}>
          <div className={styles.settingsContent}>
            <div className={styles.settingSection}>
              <label className={styles.label}>Column Width</label>
              <TextField
                className={styles.columnWidthInput}
                value={columnWidth}
                readOnly
              />
              <input
                type="range"
                defaultValue={((columnWidth - 150) / 350) * 100}
                onChange={(e) => {
                  setColumnWidth(150 + (parseInt(e.target.value) * 350) / 100);
                  setIsDirty(true);
                }}
              />
            </div>

            <div className={styles.settingSection}>
              <label className={styles.label}>Horizontal Gap</label>
              <TextField
                className={styles.columnWidthInput}
                value={gutterWidth}
                readOnly
              />
              <input
                type="range"
                defaultValue={((gutterWidth - 5) / 95) * 100}
                onChange={(e) => {
                  setGutterWidth(5 + (parseInt(e.target.value) * 95) / 100);
                  setIsDirty(true);
                }}
              />
            </div>

            <div className={styles.settingSection}>
              <label className={styles.label}>Vertical Gap</label>
              <TextField
                className={styles.columnWidthInput}
                value={gutterHeight}
                readOnly
              />
              <input
                type="range"
                defaultValue={((gutterHeight - 5) / 95) * 100}
                onChange={(e) => {
                  setGutterHeight(5 + (parseInt(e.target.value) * 95) / 100);
                  setIsDirty(true);
                }}
              />
            </div>

            <div
              className={styles.settingSectionWithToggle}
              style={{ marginLeft: '16px' }}
            >
              <label className={styles.label} htmlFor="always-show-name">
                Always show name
              </label>
              <Toggle
                id="always-show-name"
                isChecked={alwaysShowName}
                onChange={() => {
                  setAlwaysShowName((prev) => !prev);
                  setIsDirty(true);
                }}
              />
              <br />
              <label className={styles.label} htmlFor="show-attachment-name">
                Show attachment name
              </label>
              <Toggle
                id="show-attachment-name"
                isChecked={mediaCardNameField === 'attachment'}
                onChange={() => {
                  setMediaCardNameField((prev: string) => {
                    return prev === 'attachment' ? 'card' : 'attachment';
                  });
                  setIsDirty(true);
                }}
              />
              <br />
              <label className={styles.label} htmlFor="show-card-name">
                Show card name
              </label>
              <Toggle
                id="show-card-name"
                isChecked={mediaCardNameField === 'card'}
                onChange={() => {
                  setMediaCardNameField((prev: string) => {
                    return prev === 'attachment' ? 'card' : 'attachment';
                  });
                  setIsDirty(true);
                }}
              />
            </div>
          </div>
        </div>
      )}
      <StackGrid
        columnWidth={columnWidth}
        gutterWidth={gutterWidth}
        gutterHeight={gutterHeight}
        appearDelay={50}
        monitorImagesLoaded={true}
      >
        {mediaCards.map(function (mediaCard: any) {
          return (
            <div
              key={mediaCard.attachment.id}
              className={styles.mediaCard}
              style={{
                maxWidth: `${columnWidth}px`,
              }}
              onClick={(e) => {
                e.preventDefault();

                trelloIframe.popup({
                  mouseEvent: e,
                  title: `${mediaCard.name} - ${mediaCard.attachment.name}`,
                  items: [
                    {
                      text: 'Open Card',
                      callback: (t: any) => {
                        t.navigate({
                          url: mediaCard.url,
                        });
                      },
                    },
                    {
                      text: 'Open image in new tab',
                      url: mediaCard.attachment.url,
                    },
                    ...(trelloIframe.memberCanWriteToModel('board')
                      ? [
                          ...(mediaCard.isHidden
                            ? [
                                {
                                  text: 'Show in gallery',
                                  callback: (t: any) => {
                                    t.closePopup();
                                    setHiddenAttachmentIds((currentIds) => {
                                      const newValue = [...currentIds];

                                      const index = currentIds.indexOf(
                                        mediaCard.attachment.id
                                      );

                                      if (index > -1) {
                                        newValue.splice(index, 1);
                                      }

                                      t.set(
                                        'board',
                                        'shared',
                                        'gallery-view-hidden-attachments',
                                        newValue
                                      );

                                      return newValue;
                                    });
                                  },
                                },
                              ]
                            : [
                                {
                                  text: 'Hide from gallery',
                                  callback: (t: any) => {
                                    t.closePopup();
                                    setHiddenAttachmentIds((currentIds) => {
                                      const newValue = [
                                        ...currentIds,
                                        mediaCard.attachment.id,
                                      ];

                                      t.set(
                                        'board',
                                        'shared',
                                        'gallery-view-hidden-attachments',
                                        newValue
                                      );

                                      return newValue;
                                    });
                                  },
                                },
                              ]),
                        ]
                      : []),
                  ],
                });
              }}
            >
              <img
                src={mediaCard.attachment.url}
                alt={mediaCard.attachment.name ?? mediaCard.name}
              />

              {mediaCard.isHidden && (
                <div className={styles.hiddenOverlay}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="white"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M18.6247 10C19.0646 10.8986 19.25 11.6745 19.25 12C19.25 13 17.5 18.25 12 18.25C11.2686 18.25 10.6035 18.1572 10 17.9938M7 16.2686C5.36209 14.6693 4.75 12.5914 4.75 12C4.75 11 6.5 5.75 12 5.75C13.7947 5.75 15.1901 6.30902 16.2558 7.09698"
                    ></path>
                    <path
                      stroke="white"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M19.25 4.75L4.75 19.25"
                    ></path>
                    <path
                      stroke="white"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M10.409 13.591C9.53033 12.7123 9.53033 11.2877 10.409 10.409C11.2877 9.5303 12.7123 9.5303 13.591 10.409"
                    ></path>
                  </svg>
                  <div className={styles.hiddenLabel}>Hidden</div>
                </div>
              )}
              <div
                className={`${styles.details} ${
                  alwaysShowName ? styles.alwaysShowName : ''
                }`}
              >
                <div className={styles.attachamentName}>
                  {mediaCardNameField === 'attachment'
                    ? mediaCard.attachment.name
                    : mediaCard.name}
                </div>
              </div>
            </div>
          );
        })}
      </StackGrid>
    </div>
  );
};
